/**
 * Product Form Validation System
 * A centralized validation system that can be configured through Shopify theme settings
 * and shared across all product form components.
 */

window.ProductFormValidator = (function() {
  'use strict';

  // Global validation state
  const state = {
    forms: new Map(),
    validators: new Map(),
    globalValid: false
  };

  // Default selectors (can be overridden by settings)
  const defaultSelectors = {
    form: 'form.js-product-form',
    variantInput: 'input[name="id"]',
    quantityInput: 'input[name="quantity"]',
    dateInput: '.date-picker-input, input[name*="Delivery Date"]',
    dateDisplay: '.date-picker-display',
    shipToInput: 'input[name="properties[Ship To]"]',
    customFields: '[data-validation-required="true"]'
  };

  /**
   * Validator class for a single product form
   */
  class FormValidator {
    constructor(formElement, config = {}) {
      this.form = formElement;
      this.formId = formElement.id || `form-${Date.now()}`;
      this.config = { ...defaultSelectors, ...config };
      this.fields = new Map();
      this.errors = new Map();
      this.isValid = false;
      this.validationMessage = null;
      
      this.init();
    }

    init() {
      this.cacheElements();
      this.bindEvents();
      
      // Delay initial validation to ensure all elements are ready
      setTimeout(() => {
        this.validate();
      }, 100);
    }

    cacheElements() {
      // Cache form elements based on configuration
      Object.entries(this.config).forEach(([key, selector]) => {
        if (typeof selector === 'string' && (selector.includes('[') || selector.includes('.') || selector.includes('#'))) {
          const element = this.form.querySelector(selector);
          if (element) {
            this.fields.set(key, element);
          }
        }
      });

      // Check for block-level validation settings in DOM
      this.blockValidations = {};
      
      // Check variant picker block
      const variantBlock = this.form.querySelector('[data-validation-enabled].lightly-spaced-row:has(.variant-picker, variant-picker)');
      if (variantBlock) {
        this.blockValidations.variant = variantBlock.getAttribute('data-validation-enabled') === 'true';
      }
      
      // Check quantity block
      const quantityBlock = this.form.querySelector('[data-validation-enabled]:has([name="quantity"])');
      if (quantityBlock) {
        this.blockValidations.quantity = quantityBlock.getAttribute('data-validation-enabled') === 'true';
      }
      
      // Check date picker block
      const dateBlock = this.form.querySelector('[data-validation-enabled]:has(.date-picker-input, [name*="Delivery Date"])');
      if (dateBlock) {
        this.blockValidations.date = dateBlock.getAttribute('data-validation-enabled') === 'true';
      }
      
      // Check shipping instructions block
      const shipToBlock = this.form.querySelector('[data-validation-enabled][data-ship-to-block]');
      if (shipToBlock) {
        const enabledValue = shipToBlock.getAttribute('data-validation-enabled');
        this.blockValidations.shipTo = enabledValue === 'true';
      }

      // Find validation message container from the form-validation block
      // The block should have already created this container
      this.validationMessage = document.querySelector('.product-form-validation .validation-message');
      
      // If not found in the validation block, look within the form
      if (!this.validationMessage) {
        this.validationMessage = this.form.querySelector('.validation-message');
      }
      
      // Log warning if no validation message container found
      if (!this.validationMessage) {
        console.warn('ProductFormValidator: No validation message container found. Make sure the form-validation block is added to the template.');
      }
    }

    bindEvents() {
      // Bind validation events to all cached fields
      this.fields.forEach((element, key) => {
        if (element.tagName === 'INPUT' || element.tagName === 'SELECT' || element.tagName === 'TEXTAREA') {
          element.addEventListener('change', () => this.validate());
          element.addEventListener('input', debounce(() => this.validate(), 300));
        }
      });

      // Listen for custom validation events
      this.form.addEventListener('validate:field', (e) => {
        if (e.detail && e.detail.field) {
          this.validateField(e.detail.field, e.detail.value);
        }
      });

      // Listen for variant changes
      const variantPicker = this.form.querySelector('variant-picker');
      if (variantPicker) {
        variantPicker.addEventListener('variant:changed', () => this.validate());
      }

      // Listen for ship-to changes
      document.addEventListener('ship-to:change', () => this.validate());

      // Prevent form submission if invalid
      this.form.addEventListener('submit', (e) => {
        if (!this.validate()) {
          e.preventDefault();
        }
      });
    }

    validateField(fieldName, value) {
      const rules = this.config.validationRules?.[fieldName] || {};
      const errors = [];

      // Required validation
      if (rules.required && !value) {
        errors.push(rules.requiredMessage || `${fieldName} is required`);
      }

      // Min value validation
      if (rules.min !== undefined && parseFloat(value) < rules.min) {
        errors.push(rules.minMessage || `${fieldName} must be at least ${rules.min}`);
      }

      // Max value validation
      if (rules.max !== undefined && parseFloat(value) > rules.max) {
        errors.push(rules.maxMessage || `${fieldName} must be at most ${rules.max}`);
      }

      // Pattern validation
      if (rules.pattern && value && !new RegExp(rules.pattern).test(value)) {
        errors.push(rules.patternMessage || `${fieldName} format is invalid`);
      }

      // Custom validation function
      if (rules.custom && typeof rules.custom === 'function') {
        const customError = rules.custom(value, this.form);
        if (customError) {
          errors.push(customError);
        }
      }

      if (errors.length > 0) {
        this.errors.set(fieldName, errors);
      } else {
        this.errors.delete(fieldName);
      }

      return errors.length === 0;
    }

    validate() {
      this.errors.clear();

      // Use block validations from DOM if available, otherwise fall back to config
      const blockValidations = Object.keys(this.blockValidations).length > 0 
        ? this.blockValidations 
        : (this.config.blockValidations || {});

      // Validate variant selection
      const variantInput = this.fields.get('variantInput');
      const requireVariant = blockValidations.variant !== undefined ? blockValidations.variant : this.config.requireVariant;
      if (variantInput && requireVariant !== false) {
        if (!variantInput.value) {
          this.errors.set('variant', [this.config.variantRequiredMessage || 'Please select a product option']);
        }
      }

      // Validate quantity
      const quantityInput = this.fields.get('quantityInput');
      const requireQuantity = blockValidations.quantity !== undefined ? blockValidations.quantity : this.config.requireQuantity;
      if (quantityInput && requireQuantity !== false) {
        const qty = parseInt(quantityInput.value, 10);
        if (!qty || qty < 1) {
          this.errors.set('quantity', [this.config.quantityRequiredMessage || 'Quantity must be at least 1']);
        }
      }

      // Validate date
      const dateInput = this.fields.get('dateInput');
      const requireDate = blockValidations.date !== undefined ? blockValidations.date : this.config.requireDate;
      if (dateInput && requireDate) {
        if (!dateInput.value) {
          this.errors.set('date', [this.config.dateRequiredMessage || 'Please select a delivery date']);
        }
      }

      // Validate Ship To
      const shipToInput = this.fields.get('shipToInput');
      const requireShipTo = blockValidations.shipTo !== undefined ? blockValidations.shipTo : this.config.requireShipTo;
      
      if (shipToInput && requireShipTo) {
        if (!shipToInput.value || shipToInput.value.trim() === '') {
          this.errors.set('shipTo', [this.config.shipToRequiredMessage || 'Please select a shipping method']);
        }
      }

      // Validate custom required fields (handles both direct form fields and container elements)
      this.form.querySelectorAll('[data-validation-required="true"]').forEach(element => {
        let field = element;
        const fieldLabel = element.getAttribute('data-field-label');

        // If container element (no name/value), find the actual form field inside
        if (!element.name && element.value === undefined) {
          // First, check for radio/checkbox groups
          const radioCheckboxInputs = element.querySelectorAll('input[type="radio"], input[type="checkbox"]');
          if (radioCheckboxInputs.length > 0) {
            const anyChecked = Array.from(radioCheckboxInputs).some(input => input.checked);
            if (!anyChecked) {
              const message = element.getAttribute('data-validation-message')
                || `Please select ${fieldLabel || 'an option'}`;
              this.errors.set(fieldLabel || 'selection', [message]);
            }
            return; // Skip normal value check for radio/checkbox groups
          }

          // Find nested text input, textarea, or select
          field = element.querySelector('textarea, input:not([type="hidden"]):not([type="radio"]):not([type="checkbox"]), select');
        }

        const fieldName = fieldLabel || field?.name || field?.id || 'field';

        if (field && (!field.value || field.value.trim() === '')) {
          const message = element.getAttribute('data-validation-message')
            || `${fieldName} is required`;
          this.errors.set(fieldName, [message]);
        }
      });

      // Additional field validations based on config
      if (this.config.validationRules) {
        Object.entries(this.config.validationRules).forEach(([fieldName, rules]) => {
          const field = this.form.querySelector(`[name="${fieldName}"]`);
          if (field) {
            this.validateField(fieldName, field.value);
          }
        });
      }

      this.isValid = this.errors.size === 0;
      this.updateUI();
      this.broadcastState();

      return this.isValid;
    }

    updateUI() {
      // Ensure form has validation-initialized class
      if (!this.form.classList.contains('validation-initialized')) {
        this.form.classList.add('validation-initialized');
      }
      
      if (this.isValid) {
        this.hideErrors();
        this.enableActions();
      } else {
        this.showErrors();
        this.disableActions();
      }
    }

    showErrors() {
      if (!this.validationMessage) return;

      const allErrors = [];
      this.errors.forEach(errors => {
        allErrors.push(...errors);
      });

      const messageElement = this.validationMessage.querySelector('p');
      if (messageElement) {
        messageElement.innerHTML = allErrors.map(error => `<u>${error}</u>`).join('<br>');
      }
      
      this.validationMessage.classList.remove('hidden');
    }

    hideErrors() {
      if (this.validationMessage) {
        this.validationMessage.classList.add('hidden');
      }
    }

    enableActions() {
      // Enable all buttons that should be controlled by validation
      const selectors = this.config.controlledSelectors || ['.add-to-cart', '.omgrfq-btn-quotes', '[data-validation-control="true"]'];
      const selectorsString = Array.isArray(selectors) ? selectors.join(',') : selectors;
      
      const buttons = this.form.querySelectorAll(selectorsString);
      buttons.forEach(button => {
        button.classList.remove('disabled');
        button.removeAttribute('disabled');
        
        // Reset ALL visual styles
        button.style.opacity = '';
        button.style.cursor = '';
        
        // Also handle anchor tags styled as buttons
        if (button.tagName === 'A' && !button.classList.contains('omgrfq-btn-quotes')) {
          button.style.pointerEvents = '';
        }
      });
    }

    disableActions() {
      // Disable all buttons that should be controlled by validation
      const selectors = this.config.controlledSelectors || ['.add-to-cart', '.omgrfq-btn-quotes', '[data-validation-control="true"]'];
      const selectorsString = Array.isArray(selectors) ? selectors.join(',') : selectors;
      
      const buttons = this.form.querySelectorAll(selectorsString);
      buttons.forEach(button => {
        button.classList.add('disabled');
        
        // For quote buttons (OMGRFQ), add disabled attribute and visual styling
        if (button.classList.contains('omgrfq-btn-quotes')) {
          button.setAttribute('disabled', 'disabled');
          button.style.opacity = '0.5';
          button.style.cursor = 'not-allowed';
          // Note: NO pointer-events blocking for quote button
        } else {
          // For other buttons (like add to cart), keep the original behavior
          button.setAttribute('disabled', 'disabled');
          
          // Also handle anchor tags styled as buttons
          if (button.tagName === 'A') {
            button.style.pointerEvents = 'none';
            button.style.opacity = '0.5';
          }
        }
      });
    }

    broadcastState() {
      // Broadcast validation state
      const event = new CustomEvent('validation:statechange', {
        detail: {
          formId: this.formId,
          isValid: this.isValid,
          errors: Object.fromEntries(this.errors)
        },
        bubbles: true
      });
      this.form.dispatchEvent(event);

      // Update global state
      state.forms.set(this.formId, {
        isValid: this.isValid,
        errors: this.errors
      });

      // Update global validation state
      updateGlobalState();
    }

    refreshFields() {
      // Re-cache elements to pick up dynamically added fields
      this.cacheElements();
    }

    destroy() {
      // Cleanup event listeners and references
      state.forms.delete(this.formId);
      state.validators.delete(this.formId);
    }
  }

  // Utility functions
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  function updateGlobalState() {
    // Check if all forms are valid
    let allValid = true;
    state.forms.forEach(formState => {
      if (!formState.isValid) {
        allValid = false;
      }
    });
    
    state.globalValid = allValid;
    
    // Broadcast global state change
    document.dispatchEvent(new CustomEvent('validation:globalchange', {
      detail: {
        isValid: allValid,
        forms: Object.fromEntries(state.forms)
      }
    }));
  }

  // Public API
  return {
    /**
     * Initialize validation for a form
     * @param {HTMLElement|string} formOrSelector - Form element or selector
     * @param {Object} config - Validation configuration
     * @returns {FormValidator} Validator instance
     */
    init(formOrSelector, config = {}) {
      const form = typeof formOrSelector === 'string' 
        ? document.querySelector(formOrSelector)
        : formOrSelector;

      if (!form) {
        console.error('ProductFormValidator: Form not found');
        return null;
      }

      const formId = form.id || `form-${Date.now()}`;
      
      // Check if validator already exists
      if (state.validators.has(formId)) {
        return state.validators.get(formId);
      }

      const validator = new FormValidator(form, config);
      state.validators.set(formId, validator);

      return validator;
    },

    /**
     * Get validator for a specific form
     * @param {string} formId - Form ID
     * @returns {FormValidator|null} Validator instance
     */
    getValidator(formId) {
      return state.validators.get(formId) || null;
    },

    /**
     * Get global validation state
     * @returns {boolean} True if all forms are valid
     */
    isGloballyValid() {
      return state.globalValid;
    },

    /**
     * Get all form states
     * @returns {Map} Map of form states
     */
    getAllStates() {
      return new Map(state.forms);
    },

    /**
     * Validate all forms
     * @returns {boolean} True if all forms are valid
     */
    validateAll() {
      state.validators.forEach(validator => {
        validator.validate();
      });
      return state.globalValid;
    },

    /**
     * Destroy validator for a form
     * @param {string} formId - Form ID
     */
    destroy(formId) {
      const validator = state.validators.get(formId);
      if (validator) {
        validator.destroy();
      }
    },

    /**
     * Refresh fields for a specific form
     * @param {string} formId - Form ID
     */
    refreshFields(formId) {
      const validator = state.validators.get(formId);
      if (validator) {
        validator.refreshFields();
      }
    },

    /**
     * Destroy all validators
     */
    destroyAll() {
      state.validators.forEach(validator => {
        validator.destroy();
      });
      state.forms.clear();
      state.validators.clear();
    }
  };
})();